/**
 * Auto-refresh: re-fetch the open view on an interval, without the user asking.
 *
 * # Why 15 seconds
 *
 * The dashboard cannot be fresher than its sources, so this number tracks them
 * rather than taste. It used to be 30s, and the reasoning for that — recorded
 * here, because it was right at the time — was that the money figures came from
 * workers on far slower clocks: pricing every 5 minutes, rollups and provider
 * pollers every 15-60. Polling faster than that re-rendered an identical page
 * while re-running the summary aggregate against Postgres.
 *
 * That is no longer true. The collector now tails the local session files every
 * 15s (`crates/collector/src/tail.rs`) and the pricing worker runs on the same
 * cadence and drains to empty rather than one batch per tick, so usage *and* its
 * cost are both at most ~15s behind the keystroke that caused them. A slower
 * poll is now the thing that makes the page stale.
 *
 * 15s therefore matches the pipeline exactly: one poll per tail pass. Going
 * lower would re-render the same numbers, because nothing upstream produces new
 * ones faster — which is why `INTERVALS` stops there and the floor in the
 * collector is 5s. It is a default, not a law; `off` remains a legitimate
 * choice.
 *
 * # What it deliberately does not do
 *
 * It does not poll a hidden tab. A dashboard left open in a background tab for
 * a week should cost the server nothing — `visibilitychange` pauses the timer,
 * and returning to the tab refreshes immediately rather than waiting out the
 * remainder of an interval.
 */

import { useCallback, useEffect, useRef, useState } from 'react'

/** Selectable cadences. `off` is first because it is a legitimate choice. */
export const INTERVALS = [
  { label: 'Off', ms: 0 },
  { label: '15s', ms: 15_000 },
  { label: '30s', ms: 30_000 },
  { label: '2m', ms: 120_000 },
] as const

export const DEFAULT_INTERVAL_MS = 15_000

export interface AutoRefresh {
  /** Increments on every refresh. Views put this in their effect deps. */
  readonly tick: number
  /** `Date.now()` of the last completed tick, or of mount. */
  readonly lastRefreshAt: number
  /** True while a hidden tab has the timer suspended. */
  readonly paused: boolean
  /** Refresh now and restart the interval. */
  readonly refreshNow: () => void
}

/**
 * @param intervalMs cadence in milliseconds; `0` disables the timer entirely
 *        (`refreshNow` still works, so a manual refresh is always available).
 */
export function useAutoRefresh(intervalMs: number = DEFAULT_INTERVAL_MS): AutoRefresh {
  const [tick, setTick] = useState(0)
  const [lastRefreshAt, setLastRefreshAt] = useState(() => Date.now())
  const [paused, setPaused] = useState(false)

  // A ref, not state: bumping the tick must not re-run the interval effect, or
  // every refresh would tear down and recreate the timer and the cadence would
  // drift toward "immediately after each render".
  const bump = useRef(() => {})
  bump.current = () => {
    setTick((n) => n + 1)
    setLastRefreshAt(Date.now())
  }

  const refreshNow = useCallback(() => bump.current(), [])

  useEffect(() => {
    if (intervalMs <= 0) {
      setPaused(false)
      return
    }

    let timer: ReturnType<typeof setInterval> | undefined

    const start = () => {
      if (timer === undefined) timer = setInterval(() => bump.current(), intervalMs)
    }
    const stop = () => {
      if (timer !== undefined) {
        clearInterval(timer)
        timer = undefined
      }
    }

    const onVisibility = () => {
      if (document.hidden) {
        // A background tab polls nothing. Whatever it would have fetched is
        // stale by the time anyone looks at it anyway.
        stop()
        setPaused(true)
      } else {
        setPaused(false)
        // Refresh on return rather than serving whatever was on screen when
        // the tab lost focus — that figure could be arbitrarily old.
        bump.current()
        start()
      }
    }

    if (!document.hidden) start()
    else setPaused(true)

    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      stop()
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [intervalMs])

  return { tick, lastRefreshAt, paused, refreshNow }
}

/**
 * "Updated 12s ago" — deliberately coarse.
 *
 * A ticking seconds counter implies a precision the pipeline does not have:
 * the underlying figure may be minutes old regardless of when we last fetched
 * it. Rounding to the nearest sensible unit says "recently" without lying
 * about it.
 */
export function describeAge(millis: number): string {
  if (millis < 0) return 'just now'
  const seconds = Math.floor(millis / 1000)
  if (seconds < 5) return 'just now'
  if (seconds < 60) return `${seconds}s ago`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  return `${hours}h ago`
}
